/**
 * Logging types for structured logging with correlation IDs
 *
 * Purpose: Enable tracing of connection lifecycle across main thread and worker
 *
 * Why correlation IDs?
 * - Before: Can't tell which logs belong to same connection attempt
 * - After: grep "conn-12345" shows entire lifecycle (main + worker)
 *
 * Example:
 * [ConnectionOrchestrator][conn-12345][CONNECTING] Starting connection
 * [WebSocketConnectionManager][conn-12345][CONNECTING] Creating STOMP protocol
 * [WebSocketConnectionManager][conn-12345][CONNECTED] Connected in 248ms
 * [ConnectionOrchestrator][conn-12345][CONNECTED] Connection established
 */

import { ConnectionState } from './connection-state.types';

/**
 * Log level for structured logging
 */
export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/**
 * Logging context - metadata attached to every log statement
 *
 * This enables:
 * - Correlation: Link main thread and worker logs
 * - Filtering: Filter by state, attempt, user, etc.
 * - Debugging: See full context of each log
 * - Tracing: Follow connection lifecycle
 */
export interface LogContext {
  /**
   * Unique ID for this connection lifecycle
   * Format: 'conn-{timestamp}-{random}'
   * Example: 'conn-1763077460123-a3f9'
   *
   * Generated once when connection starts, passed to worker, included in all logs.
   */
  correlationId: string;

  /**
   * Server session ID (from STOMP CONNECT response)
   * Only available after CONNECTED state
   */
  sessionId?: string;

  /**
   * Browser tab ID (for multi-tab coordination)
   * Useful for debugging multi-tab scenarios
   */
  tabId?: string;

  /**
   * Reconnection attempt number (1, 2, 3, ...)
   * Resets to 0 on successful connection
   */
  attempt?: number;

  /**
   * Current connection state
   */
  state: ConnectionState;

  /**
   * Timestamp when log was created
   */
  timestamp: number;

  /**
   * User email (for multi-user debugging)
   * Useful in production for correlating user reports with logs
   */
  userEmail?: string;
}

/**
 * Minimal log context for simple scenarios
 */
export interface MinimalLogContext {
  correlationId: string;
  state: ConnectionState;
  timestamp?: number;
}

/**
 * Generate a random ID for correlation
 * Uses crypto.randomUUID if available, otherwise fallback to timestamp + random
 */
export function generateRandomId(): string {
  // Use crypto.randomUUID if available (secure and standard)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback: timestamp + random hex
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  return `${timestamp}-${random}`;
}

/**
 * Generate a correlation ID for a new connection
 */
export function generateCorrelationId(): string {
  return `conn-${generateRandomId()}`;
}

/**
 * Format a log message with context
 *
 * Example output:
 * [WebSocketConnectionManager][conn-12345][CONNECTING][attempt:2] Creating STOMP protocol
 */
export function formatLogWithContext(
  component: string,
  message: string,
  context: LogContext | MinimalLogContext
): string {
  const timestamp = context.timestamp || Date.now();
  const parts = [
    `[${component}]`,
    `[${context.correlationId}]`,
    `[${context.state}]`,
  ];

  // Add optional context
  if ('attempt' in context && context.attempt) {
    parts.push(`[attempt:${context.attempt}]`);
  }
  if ('sessionId' in context && context.sessionId) {
    parts.push(`[session:${context.sessionId.substring(0, 8)}...]`);
  }

  return `${parts.join('')} ${message}`;
}
